import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LOGO_URL } from "@/lib/format";

let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn());
  });
}

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

export function useInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const installed = isStandalone();
  const canPrompt = !!deferredPrompt;
  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    force((n) => n + 1);
    return true;
  };
  return { installed, canPrompt, ios: isIOS(), install };
}

const GuideDialog = ({ open, onClose, ios }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-sm" data-testid="install-guide-dialog">
      <DialogHeader>
        <DialogTitle>Pasang Aplikasi CU</DialogTitle>
        <DialogDescription>Tambahkan CU Tuah Talino ke layar utama HP Anda.</DialogDescription>
      </DialogHeader>
      <ol className="space-y-3 text-sm">
        {ios ? (
          <>
            <li className="flex gap-3 items-center"><Share className="h-5 w-5 text-blue-600 shrink-0" /><span>Buka di Safari, ketuk tombol <b>Bagikan</b> di bawah layar.</span></li>
            <li className="flex gap-3 items-center"><PlusSquare className="h-5 w-5 text-blue-600 shrink-0" /><span>Pilih <b>Tambah ke Layar Utama</b>, lalu ketuk <b>Tambah</b>.</span></li>
          </>
        ) : (
          <>
            <li className="flex gap-3 items-center"><span className="font-bold text-blue-600 w-5 text-center shrink-0">⋮</span><span>Buka di Chrome, ketuk menu titik tiga di kanan atas.</span></li>
            <li className="flex gap-3 items-center"><PlusSquare className="h-5 w-5 text-blue-600 shrink-0" /><span>Pilih <b>Instal aplikasi</b> atau <b>Tambahkan ke layar utama</b>.</span></li>
          </>
        )}
      </ol>
    </DialogContent>
  </Dialog>
);

export const InstallButton = ({ className = "", testid = "install-app-button" }) => {
  const { installed, ios, install } = useInstall();
  const [guide, setGuide] = useState(false);
  if (installed) return null;
  return (
    <>
      <button onClick={async () => { if (!(await install())) setGuide(true); }} data-testid={testid}
        className={`w-full flex items-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-400 text-white rounded-2xl p-4 font-semibold shadow-lg shadow-emerald-500/30 hover:brightness-105 transition-[filter] ${className}`}>
        <Download className="h-5 w-5" /> Pasang Aplikasi di Layar HP
      </button>
      <GuideDialog open={guide} onClose={() => setGuide(false)} ios={ios} />
    </>
  );
};

export const InstallBanner = () => {
  const { installed, ios, install } = useInstall();
  const [hidden, setHidden] = useState(() => localStorage.getItem("cu-install-dismissed") === "1");
  const [guide, setGuide] = useState(false);
  if (installed || hidden) return null;
  const dismiss = () => {
    localStorage.setItem("cu-install-dismissed", "1");
    setHidden(true);
  };
  return (
    <div className="relative flex items-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-400 text-white rounded-2xl p-3 pr-9 shadow-lg shadow-emerald-500/30 fade-up" data-testid="install-banner">
      <img src={LOGO_URL} alt="" className="h-11 w-11 rounded-xl bg-white keep-white object-contain p-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold">Pasang Aplikasi CU</div>
        <div className="text-[11px] text-white/90">Buka lebih cepat dari layar utama HP</div>
      </div>
      <button onClick={async () => { if (!(await install())) setGuide(true); }} data-testid="install-banner-button" className="bg-white keep-white text-xs font-bold px-3 py-2 rounded-lg" style={{ color: "#047857" }}>Pasang</button>
      <button onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Tutup" className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-white/20"><X className="h-4 w-4" /></button>
      <GuideDialog open={guide} onClose={() => setGuide(false)} ios={ios} />
    </div>
  );
};
